<template>
  <div v-if="memberHasLinkAccess" class="sf-wrapper">
    <div class="sf-container">
      <div class="sf-left">
        <div class="sf-illustration">
          <img class="sf-hero-img" src="../../assets/images/file-upload-illustration.png" alt="Core Enroll Logo" />
        </div>
        <h2 class="sf-title">Secure File Portal</h2>
        <p class="sf-subtitle">Upload your documents safely and verify with OTP.</p>
      </div>

      <div class="sf-right">
        <div class="sf-header">
          <img src="../../assets/corenroll-logo-dark.png" alt="Core Enroll Logo" class="sf-logo" />
          <h4 class="sf-heading">Secure File Upload</h4>
          <p class="sf-desc">Please upload your document securely. Enter OTP sent to your email.</p>
        </div>

        <form v-if="!successMessage" class="sf-form" @submit.prevent="handleSubmit">
          <div class="sf-form-group">
            <label class="sf-label">Title <span v-if="form.doc_type !== 'ssn'" class="input-required">*</span></label>
            <input type="text" v-model.trim="form.file_title" class="sf-input" placeholder="Enter file title" :required="form.doc_type !== 'ssn'" />
            <div v-if="apiErrors.file_title" class="error-message mt-1">{{ apiErrors.file_title[0] }}</div>
          </div>

          <div class="sf-form-group">
            <label class="sf-label">Category <span class="input-required">*</span></label>
            <select v-model="form.doc_type" class="sf-select" required>
              <option disabled value="">Select file type</option>
              <option value="member_docs">Member Docs</option>
              <option value="ssn">SSN</option>
              <option value="other">Other</option>
            </select>
            <div v-if="apiErrors.doc_type" class="error-message mt-1">{{ apiErrors.doc_type[0] }}</div>
          </div>

          <div v-if="form.doc_type === 'ssn'" class="sf-form-group">
            <label class="sf-label">Social Security Number <span class="input-required">*</span></label>
            <input type="text" v-model.trim="form.ssn" class="sf-input" placeholder="Enter 9-digit SSN" inputmode="numeric" pattern="\d{9}" maxlength="9"  />
            <div v-if="apiErrors.ssn" class="error-message mt-1">{{ apiErrors.ssn[0] }}</div>
          </div>

          <div v-if="form.doc_type !== 'ssn'" class="sf-form-group">
            <label class="sf-label">Select File <span class="input-required">*</span></label>
            <div class="sf-dropzone" @dragover.prevent @drop.prevent="onDrop" @click="triggerBrowse">
              <i class="fa fa-upload"></i>
              <p>Drag & drop files here or <span class="sf-browse">browse</span></p>
              <small>Allowed: {{ allowedFormats.join(', ') }} (max 5MB per file)</small>
              <input ref="fileInput" type="file" multiple style="display:none" @change="onFileChange" />
            </div>
            <div v-for="(f, idx) in files" :key="f.uid" class="sf-file-item">
              <div class="sf-file-info">
                <strong>{{ f.file.name }}</strong>
                <small>({{ formatBytes(f.file.size) }})</small>
              </div>
              <button type="button" class="sf-remove-btn" @click="removeFile(idx)">Remove</button>
            </div>
            <div v-if="fileErrorString" class="error-message mt-1">{{ fileErrorString }}</div>
          </div>

          <div class="sf-form-group">
            <label class="sf-label">Notes <span v-if="form.doc_type !== 'ssn'" class="input-required">*</span></label>
            <textarea v-model.trim="form.doc_notes" class="sf-textarea" rows="3" placeholder="Add notes"
              :required="form.doc_type !== 'ssn'"></textarea>
            <div v-if="apiErrors.doc_notes" class="error-message mt-1">{{ apiErrors.doc_notes[0] }}</div>
          </div>

          <div class="sf-form-group">
            <label class="sf-label">Enter OTP <span>*</span></label>
            <div class="sf-otp-container">
                <input v-for="(digit, index) in otpDigits" :key="index" v-model.number="otpDigits[index]" class="form-control otp-input" maxlength="1" min="0" max="9"
                @input="handleOtpInput(index, $event)"
                @keydown.delete="handleOtpDelete(index, $event)"
                @keydown="preventNonNumeric($event)"
                @paste="handlePaste($event)"
                ref="otpInputs"
              />
            </div>
            <div v-if="apiErrors.otp" class="error-message mt-1">{{ apiErrors.otp[0] }}</div>
          </div>

          <div class="sf-submit-container">
            <button type="submit" class="sf-upload-btn" :disabled="uploading || isFormIncomplete">
              <span v-if="uploading" class="sf-spinner"></span>
              {{ uploading ? 'Submitting...' : 'Submit' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import store from "../../store/store";

export default {
  name: "MemberFileUpload",
  data() {
    return {
      form: {
        otp: "",
        file_title: "",
        doc_type: "member_docs",
        doc_notes: "",
        ssn: "",
      },
      apiErrors: {},
      fileErrorString: "",
      files: [],
      allowedFormats: ["pdf", "jpg", "jpeg", "png", "doc", "docx", "txt", "xls", "xlsx", "csv", "pptx"],
      otpDigits: ["", "", "", "", "", ""],
      uploading: false,
      submitAttempted: false,
      successMessage: "",
      memberHasLinkAccess: false,
    };
  },
  beforeRouteEnter(to, from, next) {
    const uniqueId = to.params.uniqueId;
    const memberId = store.state.memberId;
    const corenrollURL = process.env.VUE_APP_CORENROLL_URL;

    next(async (vm) => {
      let loader = vm.$loading.show();
      if (uniqueId && memberId) {
        try {
          const response = await axios.post(`${corenrollURL}api.access/v3/file-share/check-upload-access/${uniqueId}`, { "loggedInUser": memberId });
          if (response.data.message) {
            vm.memberHasLinkAccess = true;
          } else {
            vm.handleNotify("Sorry, the link doesn't exist or expired", 'error');
            next('/');
          }
        } catch (error) {
          vm.handleNotify("Something went wrong", 'error');
          next('/');
        }
      } else {
        next('/');
      }
      loader.hide();
    });
  },
  computed: {
    isOtpValid() {
      return this.otpDigits.join("").length === 6;
    },
    isFormIncomplete() {
      if (this.form.doc_type === 'ssn') {
        return (!this.form.doc_type || !this.form.ssn || !this.isOtpValid);
      }
      return (!this.form.file_title || !this.form.doc_notes || !this.form.doc_type || this.files.length === 0 || !this.isOtpValid);
    }
  },
  methods: {
    triggerBrowse() {
      this.$refs.fileInput.click();
    },
    onFileChange(e) {
      const list = Array.from(e.target.files || []);
      this.addFiles(list);
      e.target.value = "";
    },
    onDrop(e) {
      const list = Array.from(e.dataTransfer.files || []);
      this.addFiles(list);
    },
    addFiles(list) {
      const accepted = list.filter(f => this.isAllowed(f));
      const mapped = accepted.map(f => ({ uid: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`, file: f, progress: 0, error: false }));
      this.files = this.files.concat(mapped);
    },
    isAllowed(file) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      return this.allowedFormats.includes(ext) && file.size <= 50 * 1024 * 1024;
    },
    removeFile(index) {
      this.files.splice(index, 1);
    },
    formatBytes(bytes) {
      const sizes = ["B", "KB", "MB", "GB"];
      if (bytes === 0) return "0 B";
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return (bytes / Math.pow(1024, i)).toFixed(2) + " " + sizes[i];
    },
    async handleSubmit() {
      this.uploading = true;
      this.submitAttempted = true;
      this.successMessage = "";
      const corenrollURL = process.env.VUE_APP_CORENROLL_URL;
      const formData = new FormData();

      if (this.isOtpValid) {
        this.form.otp = this.otpDigits.join("");
      }

      Object.keys(this.form).forEach(key => {
        formData.append(key, this.form[key]);
      });
      formData.append("platform_user_id", this.$store.state.memberId);
      formData.append("platform_user_type", "ADMIN");

      if (this.files && this.files.length > 0) {
        this.files.forEach((doc) => {
          formData.append('files[]', doc.file);
        });
      }

      const apiUrl = this.form.doc_type === 'ssn' 
        ? `${corenrollURL}api.access/v2/file-share/update-ssn` 
        : `${corenrollURL}api.access/v2/file-share/upload`;

      try {
        const response = await axios.post(apiUrl, formData);
        this.handleNotify(response.data.message);
        this.$router.push('/');
      } catch (error) {
        if (error.response) {
          if (error.response.status == 422) {
            this.apiErrors = error.response.data.errors;
            this.fileErrorString = this.getFileErrors(error.response.data.errors);
            setTimeout(() => {
              this.apiErrors = {};
              this.fileErrorString = "";
            }, 5000);
            return;
          }
          this.handleNotify(error.response.data.message || "Server error occurred", "error");
          return;
        }
        this.handleNotify("Something went wrong.", "error");
      } finally {
        this.uploading = false;
      }
    },
    getFileErrors(errors) {
      return Object.keys(errors)
        .filter(key => key.startsWith('files'))
        .flatMap(key => errors[key])
        .join(', ');
    },
    handleNotify(message, type = "success", title = "") {
      this.$notify({
        type: type,
        title: title,
        text: message,
      });
    },
    resetForm() {
      this.form = { file_title: "", otp: "", doc_type: "member_docs", doc_notes: "" };
      this.files = [];
      this.otpDigits = Array(6).fill("");
      this.submitAttempted = false;
    },
    handleOtpInput(index, event) {
      const value = event.target.value.replace(/\D/g, "").slice(0, 1);
      this.$set(this.otpDigits, index, value);

      if (value && index < 5) {
        this.$nextTick(() => this.$refs.otpInputs[index + 1].focus());
      }
    },
    handleOtpDelete(index, event) {
      if (event.key === "Backspace" && !event.target.value && index > 0) {
        this.$nextTick(() => this.$refs.otpInputs[index - 1].focus());
      }
    },
    preventNonNumeric(event) {
      const allowedKeys = [
        "Backspace", "Delete", "Tab", "Escape", "Enter",
        "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"
      ];

      // allow nav & system keys
      if (allowedKeys.includes(event.key)) return;

      // allow CMD + [A, C, V, X]
      if (event.metaKey && ["a", "c", "v", "x"].includes(event.key.toLowerCase()))
        return;

      // allow CTRL + [A, C, V, X]
      if (event.ctrlKey && ["a", "c", "v", "x"].includes(event.key.toLowerCase()))
        return;

      // block everything except 0–9
      if (!/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }
    },
    handlePaste(event) {
      event.preventDefault();

      const pasted = (event.clipboardData || window.clipboardData)
        .getData("text")
        .replace(/\D/g, "")
        .slice(0, 6);

      if (!pasted) return;

      for (let i = 0; i < 6; i++) {
        this.$set(this.otpDigits, i, pasted[i] || "");
      }

      const last = Math.min(pasted.length - 1, 5);

      this.$nextTick(() => {
        this.$refs.otpInputs[last].focus();
      });
    },
    fillOtpInputs(otpText) {
      console.log(otpText);
      this.otpDigits = Array(6).fill('');
      
      for (let i = 0; i < Math.min(otpText.length, 6); i++) {
        this.otpDigits[i] = otpText[i];
      }
      
      const focusIndex = Math.min(otpText.length, 5);
      this.$nextTick(() => {
        if (this.$refs.otpInputs && this.$refs.otpInputs[focusIndex]) {
          this.$refs.otpInputs[focusIndex].focus();
        }
      });
    },
 
  }
};
</script>

<style scoped>
.errors {
  color: red;
}

.sf-wrapper {
  background: linear-gradient(135deg, #f5f9ff, #ffffff);
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 10px;
}

.sf-container {
  display: flex;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 16px;
  max-width: 1100px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.sf-left {
  /* background: linear-gradient(135deg, #29bcff, #007bff); */
  background:  url('../../assets/images/bg_blue.png') no-repeat center center / cover;
  flex: 1;
  color: #fff;
  text-align: center;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.sf-illustration i {
  font-size: 54px;
  color: #fff;
  margin-bottom: 16px;
  opacity: 0.95;
}

.sf-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 8px;
}

.sf-subtitle {
  font-size: 15px;
  line-height: 1.4;
  opacity: 0.95;
}

.sf-right {
  flex: 1;
  padding: 3rem;
  background: #fff;
}

.sf-hero-img {
  width: 240px;
  margin-bottom: 20px;
}

.sf-header {
  text-align: center;
  margin-bottom: 24px;
}

.sf-logo {
  height: 35px;
  margin-bottom: 10px;
}

.sf-heading {
  font-weight: 700;
}

.sf-desc {
  font-size: 14px;
  color: #666;
}

.sf-form-group {
  margin-bottom: 1.25rem;
}

.sf-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

.sf-label span {
  color: red;
}

.sf-input,
.sf-select,
.sf-textarea {
  width: 100%;
  border: 1.5px solid #cfd9de;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 15px;
  transition: 0.25s ease;
}

.sf-input:focus,
.sf-select:focus,
.sf-textarea:focus {
  border-color: #29bcff;
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.25);
  outline: none;
}

.sf-dropzone {
  border: 2px dashed #cfd9de;
  border-radius: 12px;
  background: #f9fbff;
  text-align: center;
  padding: 30px;
  transition: 0.3s ease;
  cursor: pointer;
}

.sf-dropzone:hover {
  border-color: #29bcff;
  background: #eef5ff;
}

.sf-dropzone i {
  font-size: 22px;
  color: #29bcff;
  margin-bottom: 10px;
}

.sf-browse {
  color: #29bcff;
  font-weight: 600;
}

.sf-file-item {
  background: #fafafa;
  border: 1px solid #eef3f6;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.sf-remove-btn {
  background: rgba(231, 76, 60, 0.08);
  color: #e74c3c;
  border: 1.5px solid #e74c3c;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 13px;
  cursor: pointer;
  transition: 0.25s ease;
}

.sf-remove-btn:hover {
  background: #e74c3c;
  color: #fff;
}

.sf-otp-container {
  display: flex;
  justify-content: flex-start;
  gap: 18px;
}

.sf-otp-box {
  width: 42px;
  height: 48px;
  border: 1.5px solid #cfd9de;
  border-radius: 8px;
  text-align: center;
  font-size: 18px;
  transition: 0.25s ease;
}

.sf-otp-box:focus {
  border-color: #29bcff;
  box-shadow: 0 0 6px rgba(0, 123, 255, 0.25);
}

.sf-upload-btn {
  background-color: #29bcff;
  color: #fff;
  border: none;
  font-weight: 600;
  border-radius: 8px;
  padding: 10px 24px;
  transition: 0.3s ease;
}

.sf-upload-btn:hover {
  background-color: #13A8EB;
  transform: translateY(-1px);
}

.sf-upload-btn[disabled] {
  background-color: #A8E4FF;
  cursor: not-allowed;
}

.sf-spinner {
  border: 2px solid #fff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: inline-block;
  margin-right: 6px;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.sf-alert {
  background-color: #ffe6e6;
  border: 1px solid #ff4d4f;
  color: #b30000;
  border-radius: 8px;
  padding: 10px 15px;
  margin-bottom: 20px;
  text-align: center;
}

.sf-success {
  text-align: center;
  color: #28a745;
}

.sf-success i {
  font-size: 40px;
  margin-bottom: 10px;
}

@media (min-width: 1200px) {
  .sf-left {
    width: 600px;
  }
}

@media (max-width: 1024px) {
  .sf-left {
    min-width: 440px;
  }
}

@media (max-width: 992px) {
  .sf-right {
    padding: 2rem;
  }
}

@media (max-width: 768px) {
  .sf-container {
    flex-direction: column;
  }

  .sf-left,
  .sf-right {
    flex: 1 1 100%;
    min-width: 100%;
  }

  .sf-left {
    padding: 30px 20px;
    border-bottom: 1px solid #eaeaea;
  }

  .sf-otp-container {
    gap: 14px;
  }
}

@media (max-width: 576px) {
  .sf-right {
    padding: 1.5rem;
  }

  .sf-otp-container {
    justify-content: center;
    gap: 10px;
  }

  .sf-otp-box {
    height: 45px;
    font-size: 16px;
  }

  .sf-upload-btn {
    width: 100%;
    font-size: 14px;
  }
}

@media (max-width: 360px) {
  .sf-container {
    width: 100%;
    flex-direction: column;
  }

  .sf-left,
  .sf-right {
    min-width: auto !important;
    width: 100% !important;
    padding: 20px 15px;
  }

  .sf-left {
    border-bottom: 1px solid #eaeaea;
  }

  .sf-logo {
    height: 30px;
  }

  .sf-hero-img {
    width: 90px;
  }

  .sf-title {
    font-size: 20px;
  }

  .sf-subtitle {
    font-size: 13px;
  }

  .sf-right {
    padding: 1rem;
  }

  .sf-otp-box {
    width: 35px;
    height: 40px;
    font-size: 15px;
  }

  .sf-upload-btn {
    font-size: 13px;
    padding: 8px 16px;
  }
}

.error-message {
  color: #e74c3c;
}

.otp-input {
  text-align: center !important;
}
</style>
